import type { Funnel, FunnelReport, FunnelSource, FunnelStepInfo, FunnelStepInput } from '@growth/client';
import { type FormEvent, useState } from 'react';
import { useParams } from 'react-router';
import { Legend, slot } from '../components/charts.tsx';
import { FilterBar } from '../components/FilterBar.tsx';
import { PageHeader } from '../components/Layout.tsx';
import { noteEs } from '../components/ua.tsx';
import { Card, ChartCard, ErrorBox, Icon } from '../components/ui.tsx';
import { describeFilters, useGlobalFilters } from '../filters.ts';
import { fmtCompact, fmtInt, fmtNum, fmtPct } from '../format.ts';
import { invalidateCache, useAction, useApi, useSession } from '../session.tsx';

/** One color per source (fixed slots): ads 1, store 2, game 3. */
const SOURCE: Record<FunnelSource, { label: string; slot: number }> = {
  ads: { label: 'Anuncios (red)', slot: 1 },
  store: { label: 'Tienda (Play)', slot: 2 },
  game: { label: 'Juego (SDK)', slot: 3 },
};

/**
 * Bars are scaled within each source (the first step of each source = full width): an ad impression and a first
 * open differ by orders of magnitude, so one shared scale would hide every game step.
 */
function FunnelChart({ report }: { report: FunnelReport }) {
  const maxBySource = new Map<FunnelSource, number>();
  for (const s of report.steps) maxBySource.set(s.source, Math.max(maxBySource.get(s.source) ?? 0, s.value ?? 0));
  const sources = [...new Set(report.steps.map((s) => s.source))];
  return (
    <div className="chart">
      {sources.length >= 2 && <Legend items={sources.map((s) => ({ label: SOURCE[s].label, color: slot(SOURCE[s].slot), kind: 'rect' }))} />}
      <div role="list" aria-label="Pasos del embudo">
        {report.steps.map((s, i) => {
          const max = maxBySource.get(s.source) || 1;
          const width = s.value ? Math.max(0.5, (s.value / max) * 100) : 0;
          return (
            <div key={`${s.key}-${i}`}>
              {s.cross_source && <div className="funnel-gap" aria-hidden />}
              <div className="funnel-row" role="listitem" title={s.description}>
                <span className="funnel-label">
                  {s.label}
                  <small>
                    {SOURCE[s.source].label}
                    {s.maturity_days ? ` · instalaciones de ≥ ${s.maturity_days} día(s) (${fmtInt(s.base)})` : ''}
                  </small>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="funnel-bar" style={{ width: `${width}%`, background: slot(SOURCE[s.source].slot) }} />
                  <strong>{s.value === null ? 'sin datos' : fmtCompact(s.value)}</strong>
                </span>
                <span className="funnel-meta">
                  {i === 0
                    ? 'inicio'
                    : s.pct_of_previous === null
                      ? '—'
                      : s.pct_of_previous > 1
                        ? `${fmtPct(s.pct_of_previous, 0)} del anterior (no todos pasan por él)`
                        : `${fmtPct(s.pct_of_previous)} del anterior · pérdida ${fmtPct(s.loss_from_previous)}`}
                  {s.cross_source && i > 0 ? ' · otra fuente' : ''}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FunnelEditor({ gameId, catalog, onCreated }: { gameId: string; catalog: FunnelStepInfo[]; onCreated: (id: string) => void }) {
  const { api } = useSession();
  const action = useAction();
  const [form, setForm] = useState({ funnel_id: '', name: '' });
  const [steps, setSteps] = useState<FunnelStepInput[]>([{ key: 'game.first_open' }, { key: 'game.tutorial_complete' }]);
  const [eventName, setEventName] = useState('');
  const move = (i: number, d: number) => {
    const next = [...steps];
    const [x] = next.splice(i, 1);
    next.splice(Math.min(next.length, Math.max(0, i + d)), 0, x!);
    setSteps(next);
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const r = await action.run(() => api.createFunnel(gameId, { funnel_id: form.funnel_id.trim(), name: form.name.trim(), steps }));
    if (r) {
      invalidateCache(`funnels:${gameId}`);
      onCreated(r.funnel_id);
    }
  };
  const label = (k: string) => catalog.find((c) => c.key === k)?.label ?? k.replace(/^event:/, 'evento ');
  return (
    <Card title="Nuevo embudo" subtitle="Pasos de la red, de la tienda o del juego; «evento» = jugadores que enviaron ese evento al menos una vez.">
      <form className="stack" onSubmit={submit}>
        <div className="form-grid">
          <label className="field">
            <span>Id</span>
            <input className="input" required pattern="[a-z0-9][a-z0-9_\-]{0,39}" value={form.funnel_id} onChange={(e) => setForm({ ...form, funnel_id: e.target.value.toLowerCase() })} placeholder="onboarding" />
          </label>
          <label className="field">
            <span>Nombre</span>
            <input className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </label>
        </div>
        <ol className="stack" style={{ margin: 0, paddingLeft: 18 }}>
          {steps.map((s, i) => (
            <li key={`${s.key}-${i}`}>
              <span className="row">
                <span style={{ flex: 1 }}>{label(s.key)}</span>
                <button type="button" className="btn small ghost" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Subir">
                  ↑
                </button>
                <button type="button" className="btn small ghost" onClick={() => move(i, 1)} disabled={i === steps.length - 1} aria-label="Bajar">
                  ↓
                </button>
                <button type="button" className="btn small ghost" onClick={() => setSteps(steps.filter((_, k) => k !== i))} aria-label="Quitar">
                  ✕
                </button>
              </span>
            </li>
          ))}
        </ol>
        <div className="row">
          <select className="input" aria-label="Añadir paso" value="" onChange={(e) => e.target.value && setSteps([...steps, { key: e.target.value }])}>
            <option value="">Añadir paso…</option>
            {catalog.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label} · {SOURCE[c.source].label}
              </option>
            ))}
          </select>
          <input className="input" value={eventName} onChange={(e) => setEventName(e.target.value)} placeholder="evento (p. ej. shop_open)" aria-label="Evento" />
          <button type="button" className="btn small" disabled={!/^[a-z][a-z0-9_]{0,63}$/.test(eventName)} onClick={() => (setSteps([...steps, { key: `event:${eventName}` }]), setEventName(''))}>
            Añadir evento
          </button>
        </div>
        {action.error && <ErrorBox error={action.error} />}
        <div className="row">
          <button type="submit" className="btn primary" disabled={action.busy || steps.length < 2}>
            Crear embudo
          </button>
        </div>
      </form>
    </Card>
  );
}

export function FunnelPage() {
  const { gameId = '' } = useParams();
  const { isAdmin } = useSession();
  const filters = useGlobalFilters();
  const [funnelId, setFunnelId] = useState('default');
  const funnels = useApi<Funnel[]>(`funnels:${gameId}`, (api) => api.funnels(gameId));
  const catalog = useApi<FunnelStepInfo[]>('funnel-steps', (api) => api.funnelSteps());
  const r = useApi<FunnelReport>(`funnel:${gameId}:${funnelId}:${filters.key}`, (api, s) => api.funnelReport(gameId, funnelId, filters.params, s));
  const d = r.data;
  const onlyAds = filters.params.network === 'google_ads';

  return (
    <>
      <PageHeader
        title="Embudo"
        sub="De la impresión del anuncio a la primera compra. Cada fuente cuenta a gente distinta: los porcentajes entre fuentes son orientativos."
        right={
          <select className="input" value={funnelId} onChange={(e) => setFunnelId(e.target.value)} aria-label="Embudo">
            {(funnels.data ?? [{ funnel_id: 'default', name: 'Por defecto' } as Funnel]).map((f) => (
              <option key={f.funnel_id} value={f.funnel_id}>
                {f.name}
              </option>
            ))}
          </select>
        }
      />
      <FilterBar filters={filters} compare={false} environment={false} />
      <div className="row" style={{ marginBottom: 12 }}>
        <label className="check">
          <input type="checkbox" checked={onlyAds} onChange={(e) => filters.set({ network: e.target.checked ? 'google_ads' : null })} />
          Solo jugadores que el SDK atribuye a Google Ads (pasos del juego)
        </label>
      </div>
      {r.error && <ErrorBox error={r.error} onRetry={r.reload} />}
      {d?.steps.map((s, i) => {
        const prev = d.steps[i - 1];
        if (!prev || !s.cross_source || (s.pct_of_previous ?? 0) <= 1) return null;
        return (
          <div key={s.key} className="notice row">
            <Icon name="warning" />
            <span>
              {s.label} supera a {prev.label}: vienen de fuentes distintas ({SOURCE[prev.source].label} y {SOURCE[s.source].label}) y no cuentan a las mismas personas.
              {s.source === 'game' && !onlyAds ? ' Los pasos del juego incluyen jugadores orgánicos.' : ''}
              {s.source === 'store' ? ' La tienda recibe visitas de todas las fuentes, no solo de los anuncios.' : ''}
            </span>
          </div>
        );
      })}
      <ChartCard
        title={d?.funnel.name ?? 'Embudo'}
        subtitle={`Barras escaladas dentro de cada fuente${describeFilters(filters.params) ? ` · ${describeFilters(filters.params)}` : ''}`}
        stale={r.loading && !!d}
        table={
          d && {
            columns: [{ label: 'Paso' }, { label: 'Fuente' }, { label: 'Valor', numeric: true }, { label: '% del anterior', numeric: true }, { label: 'Pérdida', numeric: true }, { label: 'Base madura', numeric: true }],
            rows: d.steps.map((s) => [s.label, SOURCE[s.source].label, fmtNum(s.value, 1), fmtPct(s.pct_of_previous), fmtPct(s.loss_from_previous), s.base === null ? '—' : fmtInt(s.base)]),
          }
        }
      >
        {d && <FunnelChart report={d} />}
      </ChartCard>
      {d && (d.notes.length > 0 || d.steps.some((s) => s.maturity_days)) && (
        <Card title="Cómo leerlo">
          <ul className="stack small" style={{ margin: 0, paddingLeft: 18 }}>
            {d.notes.map((n) => (
              <li key={n}>{noteEs(n)}</li>
            ))}
            <li>Los pasos de retención solo cuentan instalaciones con edad suficiente (último día completo: {d.last_complete_day}) y su «% del anterior» compara ambos pasos sobre esas mismas instalaciones.</li>
            <li>Los pasos del juego cuentan hitos alcanzados hasta hoy por las instalaciones del periodo.</li>
          </ul>
        </Card>
      )}
      {isAdmin && catalog.data && <FunnelEditor gameId={gameId} catalog={catalog.data} onCreated={(id) => (funnels.reload(), setFunnelId(id))} />}
    </>
  );
}
